import React, { useState, useEffect } from 'react';
import { knowledgeBaseApiService } from '../../services/knowledgeBaseApi';

interface QuestionsBrowserProps {
  onQuestionClick: (question: string) => void;
  onClose: () => void;
}

interface Question {
  id: number;
  question: string;
  category: string;
  keywords: string[];
}

export const QuestionsBrowser: React.FC<QuestionsBrowserProps> = ({
  onQuestionClick,
  onClose,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [displayLimit, setDisplayLimit] = useState(20);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, []);

  useEffect(() => {
    filterQuestions();
  }, [questions, searchQuery, selectedCategory]);

  const loadQuestions = async () => {
    setIsLoading(true);
    try {
      const allQuestions = await knowledgeBaseApiService.getAllQuestions();
      setQuestions(allQuestions);
      // Extraire les catégories uniques
      const uniqueCategories = Array.from(new Set(allQuestions.map(q => q.category)));
      setCategories(uniqueCategories.sort());
    } catch (error) {
      console.error('Erreur lors du chargement des questions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterQuestions = () => {
    let filtered = [...questions];

    // Filtrer par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(q => q.category === selectedCategory);
    }

    // Filtrer par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(q => {
        const questionMatch = q.question.toLowerCase().includes(query);
        const keywordsMatch = q.keywords.some(kw => kw.toLowerCase().includes(query));
        const categoryMatch = q.category.toLowerCase().includes(query);
        return questionMatch || keywordsMatch || categoryMatch;
      });
    }

    setFilteredQuestions(filtered);
    setDisplayLimit(20);
    setShowAll(false);
  };

  const displayedQuestions = showAll 
    ? filteredQuestions 
    : filteredQuestions.slice(0, displayLimit);

  const hasMore = filteredQuestions.length > displayLimit && !showAll;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Base de Connaissances</h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} trouvée{filteredQuestions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Fermer"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50 border-b border-gray-200 space-y-3">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher une question..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              Toutes
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Questions List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : displayedQuestions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium">Aucune question trouvée</p>
              <p className="text-sm mt-2">Essayez de modifier vos critères de recherche</p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayedQuestions.map((question) => (
                <button
                  key={question.id}
                  onClick={() => {
                    onQuestionClick(question.question);
                    onClose();
                  }}
                  className="w-full text-left p-4 bg-white hover:bg-green-50 rounded-lg border border-gray-200 hover:border-green-300 transition-all duration-200 group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-gray-800 font-medium group-hover:text-green-700 transition-colors">
                        {question.question}
                      </p>
                      {question.keywords && question.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {question.keywords.slice(0, 3).map((keyword, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              {keyword}
                            </span>
                          ))}
                          {question.keywords.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{question.keywords.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="ml-4 px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {question.category}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Load More */}
        {hasMore && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setShowAll(true)}
              className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              Voir toutes les questions ({filteredQuestions.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

